import React, { useState, useEffect } from 'react';
import { crmAPI, demandsAPI } from '../api';
import { useAlert } from '../contexts/AlertContext';
import { 
  Building2, 
  Users, 
  MapPin, 
  Layers, 
  DollarSign, 
  Plus, 
  Search,
  X 
} from 'lucide-react';

import { MunicipalityIntelligence } from './MunicipalityIntelligence';

export const CRMManagement: React.FC = () => {
  const { showAlert, showConfirm } = useAlert();
  
  // Tabs: 'municipalities' | 'entities' | 'people' | 'allocations' | 'objects'
  const [subTab, setSubTab] = useState<'municipalities' | 'entities' | 'people' | 'allocations' | 'objects'>('municipalities');
  
  // Loading & Data states
  const [loading, setLoading] = useState(false);
  const [municipalities, setMunicipalities] = useState<any[]>([]);
  const [entities, setEntities] = useState<any[]>([]);
  const [people, setPeople] = useState<any[]>([]);
  const [allocations, setAllocations] = useState<any[]>([]);
  const [resourceObjects, setResourceObjects] = useState<any[]>([]);
  const [authorities, setAuthorities] = useState<any[]>([]);
  const [demands, setDemands] = useState<any[]>([]);
  
  // Estado para Município Selecionado (Inteligência)
  const [selectedIntelMunicipalityId, setSelectedIntelMunicipalityId] = useState<string | null>(null);

  // Filter/Search states
  const [searchQuery, setSearchQuery] = useState('');

  // Modals state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'NEW' | 'EDIT'>('NEW');
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);

  // Form states
  // Municipality Form fields
  const [mName, setMName] = useState('');
  const [mState, setMState] = useState('SP');
  const [mIbgeCode, setMIbgeCode] = useState('');
  const [mRegion, setMRegion] = useState('');
  const [mPopulation, setMPopulation] = useState('');
  const [mAddressPref, setMAddressPref] = useState('');
  const [mPhone, setMPhone] = useState('');
  const [mEmail, setMEmail] = useState('');
  const [mWebsite, setMWebsite] = useState('');
  const [mNotes, setMNotes] = useState('');

  // Person Form fields
  const [pName, setPName] = useState('');
  const [pRole, setPRole] = useState('PREFEITO');
  const [pPhone, setPPhone] = useState('');
  const [pWhatsapp, setPWhatsapp] = useState('');
  const [pEmail, setPEmail] = useState('');
  const [pType, setPType] = useState('POLITICAL');
  const [pMunicipalityId, setPMunicipalityId] = useState('');

  // Entity Form fields
  const [eName, setEName] = useState('');
  const [eType, setEType] = useState('APAE');
  const [eCnpj, setECnpj] = useState('');
  const [ePhone, setEPhone] = useState('');
  const [eEmail, setEEmail] = useState('');
  const [eAddress, setEAddress] = useState('');
  const [eActivityArea, setEActivityArea] = useState('');
  const [eResponsibleId, setEResponsibleId] = useState('');
  const [eMunicipalityId, setEMunicipalityId] = useState('');

  // ResourceObject Form fields
  const [oName, setOName] = useState('');
  const [oCategory, setOCategory] = useState('RECURSO_FINANCEIRO');
  const [oDescription, setODescription] = useState('');
  const [oThemeArea, setOThemeArea] = useState('Saúde');

  // Allocation Form fields
  const [aValue, setAValue] = useState('');
  const [aProgramName, setAProgramName] = useState('');
  const [aYear, setAYear] = useState(new Date().getFullYear());
  const [aOriginSource, setAOriginSource] = useState('ESTADUAL');
  const [aStatus, setAStatus] = useState('INDICADA');
  const [aApprovedAt, setAApprovedAt] = useState('');
  const [aPaidAt, setAPaidAt] = useState('');
  const [aDeliveredAt, setADeliveredAt] = useState('');
  const [aNotes, setANotes] = useState('');
  const [aMunicipalityId, setAMunicipalityId] = useState('');
  const [aEntityId, setAEntityId] = useState('');
  const [aAuthorityId, setAAuthorityId] = useState('');
  const [aObjectId, setAObjectId] = useState('');
  // Allocation participants helper
  const [allocationParticipants, setAllocationParticipants] = useState<{ personId: string; role: string }[]>([]);
  const [newPartPersonId, setNewPartPersonId] = useState('');
  const [newPartRole, setNewPartRole] = useState('');

  // Load all CRM datasets
  const loadCRMData = async () => {
    setLoading(true);
    try {
      const [mRes, eRes, pRes, oRes, aRes, auths, dRes] = await Promise.all([
        crmAPI.listMunicipalities(),
        crmAPI.listEntities(),
        crmAPI.listPeople(),
        crmAPI.listResourceObjects(),
        crmAPI.listAllocations(),
        demandsAPI.getAuthorities(),
        demandsAPI.list()
      ]);
      setMunicipalities(mRes);
      setEntities(eRes);
      setPeople(pRes);
      setResourceObjects(oRes);
      setAllocations(aRes);
      setAuthorities(auths);
      setDemands(dRes?.data || []);
    } catch (e) {
      console.error(e);
      showAlert('error', 'Erro', 'Erro ao carregar dados do CRM.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCRMData();
  }, []);

  // Form Resetters
  const resetMunicipalityForm = () => {
    setMName('');
    setMState('SP');
    setMIbgeCode('');
    setMRegion('');
    setMPopulation('');
    setMAddressPref('');
    setMPhone('');
    setMEmail('');
    setMWebsite('');
    setMNotes('');
  };

  const resetPersonForm = () => {
    setPName('');
    setPRole('PREFEITO');
    setPPhone('');
    setPWhatsapp('');
    setPEmail('');
    setPType('POLITICAL');
    setPMunicipalityId('');
  };

  const resetEntityForm = () => {
    setEName('');
    setEType('APAE');
    setECnpj('');
    setEPhone('');
    setEEmail('');
    setEAddress('');
    setEActivityArea('');
    setEResponsibleId('');
    setEMunicipalityId('');
  };

  const resetObjectForm = () => {
    setOName('');
    setOCategory('RECURSO_FINANCEIRO');
    setODescription('');
    setOThemeArea('Saúde');
  };

  const resetAllocationForm = () => {
    setAValue('');
    setAProgramName('');
    setAYear(new Date().getFullYear());
    setAOriginSource('ESTADUAL');
    setAStatus('INDICADA');
    setAApprovedAt('');
    setAPaidAt('');
    setADeliveredAt('');
    setANotes('');
    setAMunicipalityId('');
    setAEntityId('');
    setAAuthorityId('');
    setAObjectId('');
    setAllocationParticipants([]);
    setNewPartPersonId('');
    setNewPartRole('');
  };

  // Open Form Modal
  const handleOpenNew = () => {
    setModalType('NEW');
    setSelectedRecordId(null);
    if (subTab === 'municipalities') resetMunicipalityForm();
    else if (subTab === 'people') resetPersonForm();
    else if (subTab === 'entities') resetEntityForm();
    else if (subTab === 'objects') resetObjectForm();
    else if (subTab === 'allocations') resetAllocationForm();
    setIsModalOpen(true);
  };

  const handleOpenEdit = (record: any) => {
    setModalType('EDIT');
    setSelectedRecordId(record.id);
    
    if (subTab === 'municipalities') {
      setMName(record.name || '');
      setMState(record.state || 'SP');
      setMIbgeCode(record.ibgeCode || '');
      setMRegion(record.region || '');
      setMPopulation(record.population ? String(record.population) : '');
      setMAddressPref(record.addressPref || '');
      setMPhone(record.phone || '');
      setMEmail(record.email || '');
      setMWebsite(record.website || '');
      setMNotes(record.notes || '');
    } else if (subTab === 'people') {
      setPName(record.name || '');
      setPRole(record.role || 'PREFEITO');
      setPPhone(record.phone || '');
      setPWhatsapp(record.whatsapp || '');
      setPEmail(record.email || '');
      setPType(record.type || 'POLITICAL');
      setPMunicipalityId(record.municipalityId || '');
    } else if (subTab === 'entities') {
      setEName(record.name || '');
      setEType(record.type || 'APAE');
      setECnpj(record.cnpj || '');
      setEPhone(record.phone || '');
      setEEmail(record.email || '');
      setEAddress(record.address || '');
      setEActivityArea(record.activityArea || '');
      setEResponsibleId(record.responsibleId || '');
      setEMunicipalityId(record.municipalityId || '');
    } else if (subTab === 'objects') {
      setOName(record.name || '');
      setOCategory(record.category || 'RECURSO_FINANCEIRO');
      setODescription(record.description || '');
      setOThemeArea(record.themeArea || 'Saúde');
    } else if (subTab === 'allocations') {
      setAValue(record.value ? String(record.value) : '');
      setAProgramName(record.programName || '');
      setAYear(record.year || new Date().getFullYear());
      setAOriginSource(record.originSource || 'ESTADUAL');
      setAStatus(record.status || 'INDICADA');
      setAApprovedAt(record.approvedAt ? new Date(record.approvedAt).toISOString().split('T')[0] : '');
      setAPaidAt(record.paidAt ? new Date(record.paidAt).toISOString().split('T')[0] : '');
      setADeliveredAt(record.deliveredAt ? new Date(record.deliveredAt).toISOString().split('T')[0] : '');
      setANotes(record.notes || '');
      setAMunicipalityId(record.municipalityId || '');
      setAEntityId(record.entityId || '');
      setAAuthorityId(record.authorityId || '');
      setAObjectId(record.objectId || '');
      setAllocationParticipants(
        record.participants?.map((p: any) => ({
          personId: p.personId,
          role: p.role
        })) || []
      );
    }
    
    setIsModalOpen(true);
  };

  // Submit Handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let res;
      if (subTab === 'municipalities') {
        const data = {
          name: mName,
          state: mState,
          ibgeCode: mIbgeCode || null,
          region: mRegion || null,
          population: mPopulation ? parseInt(mPopulation) : null,
          addressPref: mAddressPref || null,
          phone: mPhone || null,
          email: mEmail || null,
          website: mWebsite || null,
          notes: mNotes || null
        };
        if (modalType === 'NEW') res = await crmAPI.createMunicipality(data);
        else res = await crmAPI.updateMunicipality(selectedRecordId!, data);
      } 
      else if (subTab === 'people') {
        const data = {
          name: pName,
          role: pRole,
          phone: pPhone || null,
          whatsapp: pWhatsapp || null,
          email: pEmail || null,
          type: pType,
          municipalityId: pMunicipalityId || null
        };
        if (modalType === 'NEW') res = await crmAPI.createPerson(data);
        else res = await crmAPI.updatePerson(selectedRecordId!, data);
      } 
      else if (subTab === 'entities') {
        const data = {
          name: eName,
          type: eType,
          cnpj: eCnpj || null,
          phone: ePhone || null,
          email: eEmail || null,
          address: eAddress || null,
          activityArea: eActivityArea || null,
          responsibleId: eResponsibleId || null,
          municipalityId: eMunicipalityId
        };
        if (modalType === 'NEW') res = await crmAPI.createEntity(data);
        else res = await crmAPI.updateEntity(selectedRecordId!, data);
      } 
      else if (subTab === 'objects') {
        const data = {
          name: oName,
          category: oCategory,
          description: oDescription || null,
          themeArea: oThemeArea
        };
        if (modalType === 'NEW') res = await crmAPI.createResourceObject(data);
        else res = await crmAPI.updateResourceObject(selectedRecordId!, data);
      } 
      else if (subTab === 'allocations') {
        const data = {
          value: parseFloat(aValue),
          programName: aProgramName || null,
          year: parseInt(String(aYear)),
          originSource: aOriginSource,
          status: aStatus,
          approvedAt: aApprovedAt || null,
          paidAt: aPaidAt || null,
          deliveredAt: aDeliveredAt || null,
          notes: aNotes || null,
          municipalityId: aMunicipalityId,
          entityId: aEntityId || null,
          authorityId: aAuthorityId,
          objectId: aObjectId,
          participants: allocationParticipants
        };
        if (modalType === 'NEW') res = await crmAPI.createAllocation(data);
        else res = await crmAPI.updateAllocation(selectedRecordId!, data);
      }

      if (res && (res.success || res.id)) {
        showAlert('success', 'Sucesso', 'Registro gravado com sucesso!');
        setIsModalOpen(false);
        loadCRMData();
      } else {
        showAlert('error', 'Erro', res.error || 'Erro desconhecido ao salvar.');
      }
    } catch (err: any) {
      console.error(err);
      showAlert('error', 'Erro', err.response?.data?.error || err.message || 'Erro ao processar requisição.');
    }
  };

  // Delete Handler
  const handleDelete = async (id: string, name: string) => {
    showConfirm('Excluir Registro', `Tem certeza que deseja remover "${name}"?`, async () => {
      try {
        let res;
        if (subTab === 'municipalities') res = await crmAPI.deleteMunicipality(id);
        else if (subTab === 'people') res = await crmAPI.deletePerson(id);
        else if (subTab === 'entities') res = await crmAPI.deleteEntity(id);
        else if (subTab === 'objects') res = await crmAPI.deleteResourceObject(id);
        else if (subTab === 'allocations') res = await crmAPI.deleteAllocation(id);
        
        if (res && res.success) {
          showAlert('success', 'Removido', 'Registro excluído com sucesso.');
          loadCRMData();
        }
      } catch (err: any) {
        console.error(err);
        showAlert('error', 'Erro', err.response?.data?.error || err.message || 'Não foi possível excluir o registro. Verifique dependências.');
      }
    }, true);
  };

  const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (ext !== '.csv') {
      showAlert('error', 'Arquivo Inválido', 'Por favor, selecione um arquivo no formato CSV (.csv)');
      return;
    }

    try {
      showAlert('info', 'Processando', 'Importando dados em lote, por favor aguarde...');
      let res;
      if (subTab === 'municipalities') {
        res = await crmAPI.importMunicipalitiesCSV(file);
      } else if (subTab === 'people') {
        res = await crmAPI.importPeopleCSV(file);
      }
      
      if (res && res.success) {
        showAlert('success', 'Importação Concluída', `Sucesso: ${res.imported} registros importados. Atualizados: ${res.updated || 0}.`);
        loadCRMData();
      }
    } catch (err: any) {
      showAlert('error', 'Erro na Importação', err.response?.data?.error || err.message);
    } finally {
      e.target.value = '';
    }
  };

  // Participant Helpers
  const addParticipant = () => {
    if (!newPartPersonId || !newPartRole) {
      showAlert('error', 'Inválido', 'Selecione a pessoa e defina o papel.');
      return;
    }
    if (allocationParticipants.some(p => p.personId === newPartPersonId)) {
      showAlert('error', 'Duplicado', 'Esta pessoa já é participante.');
      return;
    }
    setAllocationParticipants([...allocationParticipants, { personId: newPartPersonId, role: newPartRole }]);
    setNewPartPersonId('');
    setNewPartRole('');
  };

  const removeParticipant = (index: number) => {
    setAllocationParticipants(allocationParticipants.filter((_, i) => i !== index));
  };

  // Filters helper
  const filterList = (list: any[], keys: string[]) => {
    if (!searchQuery) return list;
    const cleanQuery = searchQuery.toLowerCase();
    return list.filter(item => 
      keys.some(key => {
        const val = item[key];
        if (typeof val === 'string') return val.toLowerCase().includes(cleanQuery);
        return false;
      })
    );
  };

  const filteredMunicipalities = filterList(municipalities, ['name', 'region', 'state']);
  const filteredEntities = filterList(entities, ['name', 'type', 'activityArea', 'cnpj']);
  const filteredPeople = filterList(people, ['name', 'role', 'email', 'phone']);
  const filteredObjects = filterList(resourceObjects, ['name', 'category', 'themeArea']);
  const filteredAllocations = filterList(allocations.map(a => ({
    ...a,
    mName: a.municipality?.name || '',
    authName: a.authority?.name || '',
    objName: a.object?.name || ''
  })), ['programName', 'mName', 'authName', 'objName', 'status', 'originSource']);

  if (selectedIntelMunicipalityId) {
    return (
      <MunicipalityIntelligence 
        municipalityId={selectedIntelMunicipalityId} 
        onBack={() => {
          setSelectedIntelMunicipalityId(null);
          loadCRMData();
        }}
      />
    );
  }

  return (
    <div className="space-y-6 text-left animate-in fade-in duration-200">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-sans">CRM & Gestão Parlamentar</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Gerencie municípios parceiros, contatos políticos, entidades beneficiadas e destinação de recursos institucionais.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {(subTab === 'municipalities' || subTab === 'people') && (
            <>
              <input
                type="file"
                id="crm-import-csv-file"
                accept=".csv"
                className="hidden"
                onChange={handleImportCSV}
              />
              <label
                htmlFor="crm-import-csv-file"
                className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold text-slate-700 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl shadow-sm transition-all hover:scale-105 cursor-pointer"
              >
                Importar CSV
              </label>
            </>
          )}
          <button
            onClick={handleOpenNew}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-xl shadow-lg shadow-blue-500/10 transition-all hover:scale-105"
          >
            <Plus className="w-4 h-4" />
            Novo Registro
          </button>
        </div>
      </div>

      {/* CRM Sub Tabs */}
      <div className="flex flex-wrap gap-2 p-1.5 bg-slate-100 dark:bg-slate-900/60 border border-slate-200/50 dark:border-slate-800/40 rounded-2xl max-w-fit">
        {[
          { id: 'municipalities', label: 'Municípios', icon: <MapPin className="w-3.5 h-3.5" /> },
          { id: 'entities', label: 'Entidades / Órgãos', icon: <Building2 className="w-3.5 h-3.5" /> },
          { id: 'people', label: 'Pessoas (Contatos)', icon: <Users className="w-3.5 h-3.5" /> },
          { id: 'allocations', label: 'Destinação de Recursos', icon: <DollarSign className="w-3.5 h-3.5" /> },
          { id: 'objects', label: 'Bens & Objetos', icon: <Layers className="w-3.5 h-3.5" /> }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setSubTab(tab.id as any);
              setSearchQuery('');
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              subTab === tab.id
                ? 'bg-white dark:bg-slate-950 text-blue-600 shadow-sm'
                : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-900'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search Filter Bar */}
      <div className="relative w-full max-w-md">
        <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
          <Search className="w-4 h-4" />
        </span>
        <input
          type="text"
          placeholder={`Pesquisar na aba atual...`}
          className="w-full pl-9 pr-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs focus:outline-none focus:border-blue-500"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Lists & Tables */}
      <div className="glass-panel p-5 rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800">
        {loading ? (
          <div className="py-20 text-center text-slate-400 italic">
            Carregando dados...
          </div>
        ) : (
          <>
            {/* 1. VISUAL CARDS: MUNICIPALITIES */}
            {subTab === 'municipalities' && (
              <div>
                {filteredMunicipalities.length === 0 ? (
                  <div className="p-10 text-center text-slate-500 italic">Nenhum município cadastrado.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filteredMunicipalities.map((item) => {
                      // Calcular estatísticas locais a partir de states
                      const localPeople = people.filter(p => p.municipalityId === item.id);
                      const localMayor = localPeople.find(p => p.role?.toUpperCase().includes('PREFEITO') || p.role?.toUpperCase().includes('PREFEITA'))?.name || 'Não cadastrado';
                      
                      const localEntities = entities.filter(e => e.municipalityId === item.id).length;
                      
                      // Demandas vinculadas
                      const localDemands = demands.filter(d => 
                        (d.city || '').toLowerCase() === (item.name || '').toLowerCase() || 
                        d.crmMunicipalityId === item.id
                      );
                      const totalDemandsCount = localDemands.length;
                      const activeDemandsCount = localDemands.filter(d => 
                        !['APROVADA', 'PAGA', 'CANCELADA', 'CONCLUIDA', 'RESOLVIDA', 'REJEITADA'].includes(d.status)
                      ).length;

                      // Destinações financeiras / Recursos
                      const localAllocations = allocations.filter(a => a.municipalityId === item.id);
                      const totalValueAllocated = localAllocations.reduce((acc, curr) => acc + (Number(curr.value) || 0), 0);

                      // Região e Prioridade
                      const isPriority = (item.notes || '').toLowerCase().includes('priorit') || 
                                         (item.notes || '').toLowerCase().includes('prioridade') ||
                                         item.population > 150000; // Inteligência de priorização automática

                      return (
                        <div 
                          key={item.id} 
                          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 shadow-sm hover:shadow-md dark:hover:border-slate-700 transition-all flex flex-col justify-between gap-4 text-left relative overflow-hidden"
                        >
                          {/* Banner de Prioridade */}
                          {isPriority && (
                            <div className="absolute right-0 top-0 bg-amber-500/10 text-amber-500 text-[9px] font-bold px-3 py-1 rounded-bl-2xl flex items-center gap-1 border-l border-b border-amber-500/10 uppercase tracking-wider font-mono">
                              ⭐ Prioritário
                            </div>
                          )}

                          {/* Top Section */}
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="p-1.5 bg-blue-500/10 text-blue-500 rounded-lg">
                                <MapPin className="w-4 h-4" />
                              </span>
                              <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 font-sans">
                                {item.name} - {item.state}
                              </h3>
                            </div>
                            <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider pl-8">
                              Região {item.region || 'Não informada'} • {item.population ? `${item.population.toLocaleString('pt-BR')} hab.` : 'Sem pop. cadastrada'}
                            </p>
                          </div>

                          {/* Meta Summary Grid */}
                          <div className="grid grid-cols-2 gap-3 pl-8 py-2 border-l border-slate-100 dark:border-slate-850 text-xs">
                            <div className="space-y-2 col-span-2">
                              <div className="text-[11px] text-slate-450 dark:text-slate-400 flex items-center gap-1.5">
                                <span className="text-slate-500">👤 Prefeito(a):</span>
                                <span className="font-semibold text-slate-700 dark:text-slate-200">{localMayor}</span>
                              </div>
                            </div>

                            <div className="space-y-0.5">
                              <div className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Entidades</div>
                              <div className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1">
                                🏥 {localEntities} {localEntities === 1 ? 'entidade' : 'entidades'}
                              </div>
                            </div>

                            <div className="space-y-0.5">
                              <div className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Total Destinado</div>
                              <div className="font-bold text-emerald-600 dark:text-emerald-450">
                                💰 R$ {totalValueAllocated.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </div>
                            </div>

                            <div className="space-y-0.5">
                              <div className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Demandas</div>
                              <div className="font-semibold text-slate-700 dark:text-slate-200">
                                📄 {totalDemandsCount} {totalDemandsCount === 1 ? 'cadastrada' : 'cadastradas'}
                              </div>
                            </div>

                            <div className="space-y-0.5">
                              <div className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Trâmite Ativo</div>
                              <div className="font-semibold text-amber-500 dark:text-amber-400">
                                ⏳ {activeDemandsCount} em andamento
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-850 shrink-0 text-xs mt-2 pl-8">
                            <button 
                              onClick={() => setSelectedIntelMunicipalityId(item.id)} 
                              className="px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-500 rounded-xl font-bold transition-all text-[11px]"
                            >
                              Inteligência
                            </button>
                            <button 
                              onClick={() => handleOpenEdit(item)} 
                              className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-350 rounded-xl font-bold transition-all text-[11px]"
                            >
                              Editar
                            </button>
                            <button 
                              onClick={() => handleDelete(item.id, item.name)} 
                              className="px-3 py-1.5 hover:bg-red-500/10 text-red-500 rounded-xl font-bold transition-all text-[11px]"
                            >
                              Excluir
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* 2. TABLE: ENTITIES */}
            {subTab === 'entities' && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-100 dark:bg-slate-900 text-[10px] font-bold text-slate-400 uppercase border-b border-slate-200 dark:border-slate-850">
                    <tr>
                      <th className="p-3">Entidade</th>
                      <th className="p-3">Tipo</th>
                      <th className="p-3">CNPJ</th>
                      <th className="p-3">Responsável</th>
                      <th className="p-3">Município</th>
                      <th className="p-3">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEntities.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-10 text-center text-slate-500 italic">Nenhuma entidade cadastrada.</td>
                      </tr>
                    ) : (
                      filteredEntities.map((item) => (
                        <tr key={item.id} className="border-b border-slate-100 dark:border-slate-850 hover:bg-slate-500/5">
                          <td className="p-3 font-semibold text-slate-800 dark:text-slate-200">
                            {item.name}
                          </td>
                          <td className="p-3"><span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-400 rounded font-semibold text-[10px]">{item.type}</span></td>
                          <td className="p-3 text-slate-500 font-mono">{item.cnpj || '-'}</td>
                          <td className="p-3 text-slate-500">{item.responsible?.name || '-'}</td>
                          <td className="p-3 text-slate-500">{item.municipality?.name} - {item.municipality?.state}</td>
                          <td className="p-3 space-x-3">
                            <button onClick={() => handleOpenEdit(item)} className="text-amber-500 hover:underline font-semibold">Editar</button>
                            <button onClick={() => handleDelete(item.id, item.name)} className="text-red-500 hover:underline font-semibold">Excluir</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* 3. TABLE: PEOPLE */}
            {subTab === 'people' && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-100 dark:bg-slate-900 text-[10px] font-bold text-slate-400 uppercase border-b border-slate-200 dark:border-slate-850">
                    <tr>
                      <th className="p-3">Nome</th>
                      <th className="p-3">Cargo / Papel</th>
                      <th className="p-3">Tipo</th>
                      <th className="p-3">Contato</th>
                      <th className="p-3">Município</th>
                      <th className="p-3">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPeople.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-10 text-center text-slate-500 italic">Nenhuma pessoa cadastrada.</td>
                      </tr>
                    ) : (
                      filteredPeople.map((item) => (
                        <tr key={item.id} className="border-b border-slate-100 dark:border-slate-850 hover:bg-slate-500/5">
                          <td className="p-3 font-semibold text-slate-800 dark:text-slate-200">
                            {item.name}
                          </td>
                          <td className="p-3 text-slate-550 font-semibold">{item.role}</td>
                          <td className="p-3">
                            <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded font-semibold text-[10px] text-slate-650">{item.type}</span>
                          </td>
                          <td className="p-3 text-slate-500">
                            <div>{item.phone || item.whatsapp || '-'}</div>
                            <div className="text-[10px] opacity-75">{item.email}</div>
                          </td>
                          <td className="p-3 text-slate-500">{item.municipality?.name ? `${item.municipality.name} - ${item.municipality.state}` : '-'}</td>
                          <td className="p-3 space-x-3">
                            <button onClick={() => handleOpenEdit(item)} className="text-amber-500 hover:underline font-semibold">Editar</button>
                            <button onClick={() => handleDelete(item.id, item.name)} className="text-red-500 hover:underline font-semibold">Excluir</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* 4. TABLE: RESOURCE OBJECTS */}
            {subTab === 'objects' && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-100 dark:bg-slate-900 text-[10px] font-bold text-slate-400 uppercase border-b border-slate-200 dark:border-slate-850">
                    <tr>
                      <th className="p-3">Bem / Objeto</th>
                      <th className="p-3">Categoria</th>
                      <th className="p-3">Área Temática</th>
                      <th className="p-3">Descrição</th>
                      <th className="p-3">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredObjects.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="p-10 text-center text-slate-500 italic">Nenhum bem de recurso cadastrado.</td>
                      </tr>
                    ) : (
                      filteredObjects.map((item) => (
                        <tr key={item.id} className="border-b border-slate-100 dark:border-slate-850 hover:bg-slate-500/5">
                          <td className="p-3 font-semibold text-slate-800 dark:text-slate-200">
                            {item.name}
                          </td>
                          <td className="p-3">
                            <span className="px-2 py-0.5 bg-amber-500/10 text-amber-600 rounded font-semibold text-[10px]">
                              {item.category.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="p-3 font-semibold text-blue-500">{item.themeArea}</td>
                          <td className="p-3 text-slate-500 truncate max-w-xs">{item.description || '-'}</td>
                          <td className="p-3 space-x-3">
                            <button onClick={() => handleOpenEdit(item)} className="text-amber-500 hover:underline font-semibold">Editar</button>
                            <button onClick={() => handleDelete(item.id, item.name)} className="text-red-500 hover:underline font-semibold">Excluir</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* 5. TABLE: ALLOCATIONS */}
            {subTab === 'allocations' && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-100 dark:bg-slate-900 text-[10px] font-bold text-slate-400 uppercase border-b border-slate-200 dark:border-slate-850">
                    <tr>
                      <th className="p-3">Valor</th>
                      <th className="p-3">Beneficiário / Destino</th>
                      <th className="p-3">Programa / Ano</th>
                      <th className="p-3">Fonte / Autoridade</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAllocations.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-10 text-center text-slate-500 italic">Nenhuma destinação cadastrada.</td>
                      </tr>
                    ) : (
                      filteredAllocations.map((item) => (
                        <tr key={item.id} className="border-b border-slate-100 dark:border-slate-850 hover:bg-slate-500/5">
                          <td className="p-3 font-bold text-slate-800 dark:text-slate-100">
                            {Number(item.value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </td>
                          <td className="p-3">
                            <div className="font-semibold text-slate-700 dark:text-slate-300">{item.municipality?.name}</div>
                            <div className="text-[10px] text-slate-500">{item.entity?.name || 'Prefeitura Direta'}</div>
                          </td>
                          <td className="p-3 text-slate-500">
                            <div>{item.programName || '-'}</div>
                            <div className="font-semibold text-[10px] text-slate-400 font-mono">{item.year}</div>
                          </td>
                          <td className="p-3 text-slate-550">
                            <div className="font-semibold text-[10px] text-blue-500">{item.originSource}</div>
                            <div className="text-slate-500">{item.authority?.name || '-'}</div>
                          </td>
                          <td className="p-3">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 font-bold rounded text-[10px] ${
                              item.status === 'PAGA' || item.status === 'ENTREGUE'
                                ? 'bg-emerald-500/10 text-emerald-500'
                                : item.status === 'APROVADA'
                                ? 'bg-blue-500/10 text-blue-500'
                                : item.status === 'CANCELADA'
                                ? 'bg-red-500/10 text-red-500'
                                : 'bg-amber-500/10 text-amber-500'
                            }`}>
                              {item.status}
                            </span>
                          </td>
                          <td className="p-3 space-x-3">
                            <button onClick={() => handleOpenEdit(item)} className="text-amber-500 hover:underline font-semibold">Editar</button>
                            <button onClick={() => handleDelete(item.id, `${item.municipality?.name} - ${item.year}`)} className="text-red-500 hover:underline font-semibold">Excluir</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* 🔮 DETAILED CRUD MODAL */}
      {/* ──────────────────────────────────────────────────────── */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-[200] overflow-y-auto p-4 md:p-6 flex items-center justify-center">
          <div className="bg-white dark:bg-slate-900 rounded-3xl w-full max-w-2xl p-6 shadow-2xl relative text-left border border-slate-200 dark:border-slate-800 my-auto">
            
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="absolute right-4 top-4 p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl text-slate-400 dark:text-slate-500 hover:text-slate-650 dark:hover:text-slate-300 transition-all focus:outline-none"
            >
              <X className="w-5 h-5" />
            </button>
 
            <div className="mb-5 pr-8">
              <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                {modalType === 'NEW' ? 'Novo Registro' : 'Editar Registro'} - {subTab === 'municipalities' ? 'Município' : subTab === 'people' ? 'Pessoa' : subTab === 'entities' ? 'Entidade' : subTab === 'objects' ? 'Objeto' : 'Destinação'}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">Preencha as informações necessárias no formulário abaixo.</p>
            </div>
 
            <form onSubmit={handleSubmit} className="space-y-6 text-xs">
              <div className="space-y-4">
              
              {/* 1. FORM: MUNICIPALITY */}
              {subTab === 'municipalities' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Nome do Município *</label>
                    <input type="text" required value={mName} onChange={e => setMName(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Estado (UF)</label>
                    <input type="text" value={mState} onChange={e => setMState(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Código IBGE</label>
                    <input type="text" value={mIbgeCode} onChange={e => setMIbgeCode(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Região (Ex: Centro-Oeste)</label>
                    <input type="text" value={mRegion} onChange={e => setMRegion(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">População</label>
                    <input type="number" value={mPopulation} onChange={e => setMPopulation(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Telefone Geral</label>
                    <input type="text" value={mPhone} onChange={e => setMPhone(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-slate-400 mb-1 font-medium">E-mail Oficial</label>
                    <input type="email" value={mEmail} onChange={e => setMEmail(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-slate-400 mb-1 font-medium">Endereço da Prefeitura</label>
                    <input type="text" value={mAddressPref} onChange={e => setMAddressPref(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-slate-400 mb-1 font-medium">Website Oficial</label>
                    <input type="text" value={mWebsite} onChange={e => setMWebsite(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-slate-400 mb-1 font-medium">Notas / Observações</label>
                    <textarea rows={3} value={mNotes} onChange={e => setMNotes(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                </div>
              )}

              {/* 2. FORM: PERSON */}
              {subTab === 'people' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Nome Completo *</label>
                    <input type="text" required value={pName} onChange={e => setPName(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Cargo / Função *</label>
                    <input type="text" required placeholder="Ex: Prefeito, Chefe de Gabinete, Parceiro..." value={pRole} onChange={e => setPRole(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Tipo de Cargo</label>
                    <select value={pType} onChange={e => setPType(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none">
                      <option value="POLITICAL">Político (Ex: Prefeitos, Vereadores)</option>
                      <option value="ADMINISTRATIVE">Administrativo (Ex: Secretários, Diretores)</option>
                      <option value="SOCIAL">Social / Comunitário</option>
                      <option value="PARTNER">Parceiro / Liderança Regional</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Município Vinculado</label>
                    <select value={pMunicipalityId} onChange={e => setPMunicipalityId(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none">
                      <option value="">Nenhum município específico</option>
                      {municipalities.map(m => (
                        <option key={m.id} value={m.id}>{m.name} - {m.state}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Telefone Fixo</label>
                    <input type="text" value={pPhone} onChange={e => setPPhone(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Whatsapp</label>
                    <input type="text" value={pWhatsapp} onChange={e => setPWhatsapp(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-slate-400 mb-1 font-medium">E-mail</label>
                    <input type="email" value={pEmail} onChange={e => setPEmail(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                </div>
              )}

              {/* 3. FORM: ENTITY */}
              {subTab === 'entities' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Nome da Entidade *</label>
                    <input type="text" required value={eName} onChange={e => setEName(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Tipo de Entidade *</label>
                    <select value={eType} onChange={e => setEType(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none">
                      <option value="APAE">APAE</option>
                      <option value="SANTA_CASA">Santa Casa</option>
                      <option value="HOSPITAL">Hospital</option>
                      <option value="ASILO">Lar / Asilo</option>
                      <option value="ONG">ONG</option>
                      <option value="ASSOCIACAO">Associação</option>
                      <option value="OUTRO">Outro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">CNPJ</label>
                    <input type="text" value={eCnpj} onChange={e => setECnpj(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Área de Atuação</label>
                    <input type="text" placeholder="Ex: Saúde, Reabilitação, Idosos..." value={eActivityArea} onChange={e => setEActivityArea(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Município *</label>
                    <select required value={eMunicipalityId} onChange={e => setEMunicipalityId(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none">
                      <option value="">Selecione o município...</option>
                      {municipalities.map(m => (
                        <option key={m.id} value={m.id}>{m.name} - {m.state}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Responsável (Pessoa no CRM)</label>
                    <select value={eResponsibleId} onChange={e => setEResponsibleId(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none">
                      <option value="">Nenhum selecionado</option>
                      {people.map(p => (
                        <option key={p.id} value={p.id}>{p.name} ({p.role})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Telefone</label>
                    <input type="text" value={ePhone} onChange={e => setEPhone(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">E-mail</label>
                    <input type="email" value={eEmail} onChange={e => setEEmail(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-slate-400 mb-1 font-medium">Endereço Completo</label>
                    <input type="text" value={eAddress} onChange={e => setEAddress(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                </div>
              )}

              {/* 4. FORM: RESOURCE OBJECT */}
              {subTab === 'objects' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Nome do Bem / Objeto *</label>
                    <input type="text" required placeholder="Ex: Ambulância UTI, Trator Agrícola, Custeio de Saúde" value={oName} onChange={e => setOName(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Categoria *</label>
                    <select value={oCategory} onChange={e => setOCategory(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none">
                      <option value="RECURSO_FINANCEIRO">Recurso Financeiro / Custeio</option>
                      <option value="VEICULO">Veículo / Ambulância</option>
                      <option value="MAQUINARIO">Maquinário Agrícola / Rodoviário</option>
                      <option value="EQUIPAMENTO">Equipamento Médico / Hospitalar</option>
                      <option value="OUTRO">Outro Bem Material</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-slate-400 mb-1 font-medium">Área Temática *</label>
                    <select value={oThemeArea} onChange={e => setOThemeArea(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none">
                      <option value="Saúde">Saúde</option>
                      <option value="Educação">Educação</option>
                      <option value="Infraestrutura">Infraestrutura</option>
                      <option value="Segurança Pública">Segurança Pública</option>
                      <option value="Assistência Social">Assistência Social</option>
                      <option value="Esporte">Esporte</option>
                      <option value="Cultura">Cultura</option>
                      <option value="Outros">Outros</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-slate-400 mb-1 font-medium">Descrição Técnica</label>
                    <textarea rows={3} value={oDescription} onChange={e => setODescription(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                </div>
              )}

              {/* 5. FORM: ALLOCATION */}
              {subTab === 'allocations' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Valor da Destinação *</label>
                    <input type="number" step="0.01" required placeholder="0.00" value={aValue} onChange={e => setAValue(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Ano *</label>
                    <input type="number" required value={aYear} onChange={e => setAYear(parseInt(e.target.value))} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Nome do Programa / Convênio</label>
                    <input type="text" placeholder="Ex: Emenda Federal 2026, Convênio Saúde SP" value={aProgramName} onChange={e => setAProgramName(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Origem do Recurso *</label>
                    <select value={aOriginSource} onChange={e => setAOriginSource(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none">
                      <option value="ESTADUAL">Emenda Estadual (Dani Alonso)</option>
                      <option value="FEDERAL">Emenda Federal (Capitão Augusto)</option>
                      <option value="CONVENIO">Parceria / Convênio Especial</option>
                      <option value="PROPRIO">Recurso Próprio do Gabinete</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Status do Trâmite *</label>
                    <select value={aStatus} onChange={e => setAStatus(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none">
                      <option value="INDICADA">Indicada (Fase de Ofício)</option>
                      <option value="APROVADA">Aprovada (Diário Oficial)</option>
                      <option value="CONVENIO_ASSINADO">Convênio Assinado (SICONV/MUNICÍPIO)</option>
                      <option value="PAGA">Paga (Recurso Depositado)</option>
                      <option value="ENTREGUE">Entregue (Objeto Entregue ao Município)</option>
                      <option value="CANCELADA">Cancelada / Rejeitada</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Município Destino *</label>
                    <select required value={aMunicipalityId} onChange={e => setAMunicipalityId(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none">
                      <option value="">Selecione...</option>
                      {municipalities.map(m => (
                        <option key={m.id} value={m.id}>{m.name} - {m.state}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Entidade Beneficiada (Opcional)</label>
                    <select value={aEntityId} onChange={e => setAEntityId(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none">
                      <option value="">Prefeitura Direta (Sem Entidade)</option>
                      {entities.filter(ent => !aMunicipalityId || ent.municipalityId === aMunicipalityId).map(ent => (
                        <option key={ent.id} value={ent.id}>{ent.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Autoridade Remetente *</label>
                    <select required value={aAuthorityId} onChange={e => setAAuthorityId(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none">
                      <option value="">Selecione...</option>
                      {authorities.map(auth => (
                        <option key={auth.id} value={auth.id}>{auth.role} {auth.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-slate-400 mb-1 font-medium">Bem / Objeto Destinado *</label>
                    <select required value={aObjectId} onChange={e => setAObjectId(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none">
                      <option value="">Selecione...</option>
                      {resourceObjects.map(obj => (
                        <option key={obj.id} value={obj.id}>[{obj.themeArea}] {obj.name} ({obj.category})</option>
                      ))}
                    </select>
                  </div>

                  {/* Dates Grid */}
                  <div className="md:col-span-2 grid grid-cols-3 gap-3 bg-slate-50 dark:bg-slate-950 p-3 rounded-2xl border border-slate-200 dark:border-slate-900">
                    <div>
                      <label className="block text-slate-400 mb-1 font-semibold">Data Indicação/Aprovação</label>
                      <input type="date" value={aApprovedAt} onChange={e => setAApprovedAt(e.target.value)} className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-2" />
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1 font-semibold">Data do Pagamento</label>
                      <input type="date" value={aPaidAt} onChange={e => setAPaidAt(e.target.value)} className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-2" />
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1 font-semibold">Data de Entrega Física</label>
                      <input type="date" value={aDeliveredAt} onChange={e => setADeliveredAt(e.target.value)} className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-2" />
                    </div>
                  </div>

                  {/* CRM Participants Relation Manager */}
                  <div className="md:col-span-2 bg-slate-50/50 dark:bg-slate-900/30 p-4 rounded-2xl border border-slate-200 dark:border-slate-800">
                    <span className="block font-semibold text-slate-700 dark:text-slate-300 mb-2">Vincular Pessoas à Destinação (Participantes)</span>
                    
                    <div className="flex gap-2 mb-3">
                      <select value={newPartPersonId} onChange={e => setNewPartPersonId(e.target.value)} className="flex-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-2">
                        <option value="">Selecione...</option>
                        {people.map(p => (
                          <option key={p.id} value={p.id}>{p.name} ({p.role})</option>
                        ))}
                      </select>
                      <input type="text" placeholder="Papel (Ex: Gestor, Fiscal)" value={newPartRole} onChange={e => setNewPartRole(e.target.value)} className="flex-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-2" />
                      <button type="button" onClick={addParticipant} className="px-4 bg-slate-900 dark:bg-slate-100 hover:bg-slate-800 dark:hover:bg-white text-white dark:text-slate-950 font-bold rounded-xl">Vincular</button>
                    </div>

                    <div className="space-y-1">
                      {allocationParticipants.length === 0 ? (
                        <p className="text-slate-400 italic text-[11px]">Nenhuma pessoa vinculada a este recurso.</p>
                      ) : (
                        allocationParticipants.map((p, i) => {
                          const personObj = people.find(item => item.id === p.personId);
                          return (
                            <div key={i} className="flex justify-between items-center bg-white dark:bg-slate-900 p-2 rounded-xl border border-slate-200 dark:border-slate-850">
                              <span className="font-semibold text-slate-700 dark:text-slate-200">
                                {personObj?.name || 'Pessoa Desconhecida'} - <span className="opacity-75">{p.role}</span>
                              </span>
                              <button type="button" onClick={() => removeParticipant(i)} className="text-red-500 hover:underline">Remover</button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-slate-400 mb-1 font-medium">Notas da Destinação / Trâmite</label>
                    <textarea rows={3} value={aNotes} onChange={e => setANotes(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none" />
                  </div>
                </div>
              )}

              </div>
              
              {/* Action buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-150 dark:border-slate-850">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-900 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-xl font-bold text-slate-700 dark:text-slate-300"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold"
                >
                  Salvar
                </button>
              </div>

            </form>
          </div>
        </div>
      )}
    </div>
  );
};
